/**
 * Page navigation for a list.
 *
 * Shows the range and the total rather than page numbers alone: "showing
 * 51–100 of 340" tells the reader where they are and how much is left, which a
 * bare "page 2" does not.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.15
 */
import { useTranslation } from 'react-i18next';

import { formatNumber } from '../../lib/format';
import { PAGE_SIZES } from '../../lib/table-state';

import { Button } from './Button';
import { Icon } from './Icon';
import { pageSizeHandler } from './page-size-handler';
import { Select } from './Select';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: PaginationProps): React.JSX.Element {
  const { t } = useTranslation();

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  /* Clamped to the total: the last page is usually partial, and "showing
     301–350 of 340" is the kind of detail that makes a reader distrust the
     rest of the figures. */
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-3">
      {/* `aria-live` so the range is announced after a page change — otherwise
          a keyboard user hears nothing and cannot tell whether it worked. */}
      <p aria-live="polite" className="text-body text-xs">
        {t('pagination.summary', {
          from: formatNumber(from),
          to: formatNumber(to),
          total: formatNumber(total),
        })}
      </p>

      <div className="flex items-center gap-2">
        <Select
          value={String(pageSize)}
          onValueChange={pageSizeHandler(onPageSizeChange)}
          options={PAGE_SIZES.map((size) => ({
            value: String(size),
            label: String(size),
          }))}
          ariaLabel={t('pagination.rowsPerPage')}
          className="w-24"
        />

        <Button
          variant="secondary"
          size="sm"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1);
          }}
          aria-label={t('pagination.previous')}
        >
          <Icon name="arrow" className="rotate-180" />
        </Button>

        <span className="text-body text-xs whitespace-nowrap">
          {t('pagination.page', { page: formatNumber(page), pages: formatNumber(pages) })}
        </span>

        <Button
          variant="secondary"
          size="sm"
          disabled={page >= pages}
          onClick={() => {
            onPageChange(page + 1);
          }}
          aria-label={t('pagination.next')}
        >
          <Icon name="arrow" />
        </Button>
      </div>
    </div>
  );
}
